import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import SleepVideoV3Service from '../../services/SleepVideoV3Service';

const DEFAULT_POSITION = { x: 0.78, y: 0.72 };
const DEFAULT_SIZE = 0.14;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clampPosition(nextPosition, nextSize) {
  const max = Math.max(0, 1 - nextSize);
  return {
    x: clamp(nextPosition?.x ?? DEFAULT_POSITION.x, 0, max),
    y: clamp(nextPosition?.y ?? DEFAULT_POSITION.y, 0, max),
  };
}

function basename(fileName) {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

export default function QrCodeManagerDialog({
  open,
  onClose,
  onChanged,
}) {
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [dragging, setDragging] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SleepVideoV3Service.listQrCodes();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadItems();
  }, [open]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const resetEditor = () => {
    setEditingId(null);
    setFile(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl('');
    setName('');
    setPosition(DEFAULT_POSITION);
    setSize(DEFAULT_SIZE);
    setDragging(false);
    setError(null);
  };

  const startNew = () => {
    resetEditor();
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(selected.type)) {
      setError('QR image must be PNG, JPG, or WebP');
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setError('QR image must be 10MB or smaller');
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(selected);
    objectUrlRef.current = url;
    setFile(selected);
    setPreviewUrl(url);
    setEditingId(null);
    setName(basename(selected.name));
    setPosition(DEFAULT_POSITION);
    setSize(DEFAULT_SIZE);
    setError(null);
  };

  const editItem = (item) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setEditingId(item.id);
    setFile(null);
    setPreviewUrl(item.url || '');
    setName(item.name || '');
    const nextSize = item.size || DEFAULT_SIZE;
    setPosition(clampPosition(item.position, nextSize));
    setSize(nextSize);
    setError(null);
  };

  const updatePositionFromPointer = (event) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextX = (event.clientX - rect.left) / rect.width - size / 2;
    const nextY = (event.clientY - rect.top) / rect.height - size / 2;
    setPosition(clampPosition({ x: nextX, y: nextY }, size));
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    updatePositionFromPointer(event);
  };

  const handlePointerMove = (event) => {
    if (!dragging) return;
    updatePositionFromPointer(event);
  };

  const handlePointerUp = () => {
    setDragging(false);
  };

  const handleSizeChange = (_, value) => {
    const nextSize = value / 100;
    setSize(nextSize);
    setPosition((prev) => clampPosition(prev, nextSize));
  };

  const save = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (!editingId && !file) {
      setError('Upload a QR image first');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let saved;
      const renderPosition = clampPosition(position, size);
      if (editingId) {
        saved = await SleepVideoV3Service.updateQrCode(editingId, {
          name: trimmedName,
          position: renderPosition,
          size,
        });
      } else {
        const uploadTarget = await SleepVideoV3Service.requestQrCodePresignedUrl({
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'image/png',
        });
        await axios.put(uploadTarget.presignedUrl, file, {
          headers: { 'Content-Type': uploadTarget.contentType || file.type || 'image/png' },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        saved = await SleepVideoV3Service.createQrCode({
          name: trimmedName,
          objectName: uploadTarget.objectName,
          fileName: file.name,
          fileSize: file.size,
          contentType: uploadTarget.contentType || file.type || 'image/png',
          position: renderPosition,
          size,
        });
      }
      const data = await SleepVideoV3Service.listQrCodes();
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems(nextItems);
      onChanged?.(nextItems, saved);
      resetEditor();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save QR code');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    setSaving(true);
    setError(null);
    try {
      await SleepVideoV3Service.deleteQrCode(item.id);
      const nextItems = items.filter((x) => x.id !== item.id);
      setItems(nextItems);
      onChanged?.(nextItems, null);
      if (editingId === item.id) resetEditor();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete QR code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitle>QR Codes</DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/png,image/jpeg,image/webp'
          onChange={handleFileSelected}
          style={{ display: 'none' }}
        />
        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: '1 1 58%', minWidth: 0 }}>
            <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
              <Button
                variant='contained'
                startIcon={<AddPhotoAlternateIcon />}
                onClick={startNew}
                disabled={saving}
              >
                Upload QR
              </Button>
              {(previewUrl || editingId) && (
                <Button onClick={resetEditor} disabled={saving}>
                  Clear
                </Button>
              )}
            </Stack>

            <Box
              ref={previewRef}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                borderRadius: 1,
                bgcolor: '#101418',
                border: 1,
                borderColor: 'divider',
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }}
            >
              {previewUrl ? (
                <Box
                  component='img'
                  src={previewUrl}
                  alt='QR preview'
                  draggable={false}
                  onPointerDown={handlePointerDown}
                  sx={{
                    position: 'absolute',
                    left: `${position.x * 100}%`,
                    top: `${position.y * 100}%`,
                    width: `${size * 100}%`,
                    aspectRatio: '1 / 1',
                    objectFit: 'contain',
                    cursor: dragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    touchAction: 'none',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                    outline: '2px solid rgba(255,255,255,0.85)',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <Typography variant='body2'>Upload or edit a QR code</Typography>
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              label='Name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving || (!previewUrl && !editingId)}
              sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Size
              </Typography>
              <Slider
                value={Math.round(size * 100)}
                min={4}
                max={50}
                onChange={handleSizeChange}
                disabled={saving || !previewUrl}
                valueLabelDisplay='auto'
                valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={saving || !previewUrl}
              sx={{ mt: 1 }}
            >
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Save QR Code'}
            </Button>
          </Box>

          <Divider flexItem orientation='vertical' sx={{ display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ flex: '1 1 42%', minWidth: 0 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
              Saved QR Codes
            </Typography>
            {items.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No QR codes saved.
              </Typography>
            ) : (
              <List dense disablePadding>
                {items.map((item) => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    secondaryAction={
                      <Stack direction='row' spacing={0.5}>
                        <Tooltip title='Edit'>
                          <IconButton edge='end' size='small' onClick={() => editItem(item)}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            edge='end'
                            size='small'
                            color='error'
                            onClick={() => remove(item)}
                            disabled={saving}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                    sx={{
                      py: 0.75,
                      pr: 9,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemAvatar>
                      <Box
                        component='img'
                        src={item.url}
                        alt=''
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'contain',
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.name}
                      secondary={`Size ${Math.round((item.size || DEFAULT_SIZE) * 100)}%`}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
